<template>
  <div>
    <transition name="fade">
      <LoadingView
        v-if="clubLoading"
        :loading="loading"
      />
    </transition>

    <transition name="fade">
      <div
        v-if="!clubLoading"
        class="w-full h-screen min-h-screen text-gray-700"
      >
        <div
          class="bg-center bg-cover w-full overflow-hidden"
          style="
            background-image: url('https://tools.playmore.golf/Compare/img/bg2.jpg');
            height: 400px;
          "
        />

        <div class="">
          <div class="max-w-5xl ml-auto mr-auto -m-36 pl-2 pr-2 md:p-0">
            <div
              v-if="club !== null && !club.Authorized"
              class="flex justify-between items-center bg-white overflow-hidden mb-2 text-gray-800 border text-sm p-1"
            >
              <div class="p-3 pl-4 pr-4 flex items-center leading-none">
                <i
                  class="fas fa-info-circle text-gray-800 mr-2"
                  style="margin-top: 1px"
                />
                <span class="mr-2 font-semibold">Not logged in</span>
                You must be logged in to be able to access live data.
              </div>

              <div class="mr-1">
                <a
                  class=""
                  href="https://members.playmore.golf"
                  target="_blank"
                >
                  <div
                    class="bg-gray-800 text-white hover:bg-gray-900 leading-none p-2 pl-2 pr-2 transition-all duration-500"
                  >
                    Log in
                  </div>
                </a>
              </div>
            </div>

            <div class="shadow">
              <div class="bg-gradient-to-b from-gray-700 to to-gray-900 t-lg px-9 py-8">
                <div class="flex justify-between items-center mb-4">
                  <div class="flex items-center">
                    <img
                      src="https://www.ix.golf/hs-fs/hubfs/IX-Logo-3-3.png?width=500&height=500&name=IX-Logo-3-3.png"
                      alt="IX Logo"
                      class="h-12 w-12 mr-4 object-contain"
                    />
                    <div
                      class="font-bold text-white heading leading-none"
                      :class="club !== null && club.ClubName.length > 22 ? 'text-3xl' : 'text-4xl'"
                    >
                      <span v-if="club === null">IX Flexible Membership Simulator</span>
                      <span v-else>{{ club.ClubName }} IX Membership Simulator</span>
                    </div>
                  </div>
                </div>

                <div class="text-sm text-gray-100 mt-8 leading-relaxed">
                  <div>
                    Explore the possible outcomes of your own flexible membership platform using our
                    simulator and compare the results to what you could achieve with
                    <a
                      href="https://playmore.golf"
                      target="_blank"
                      class="hover:text-white cursor-pointer transition-all duration-500 font-semibold"
                    >
                      IX
                    </a>
                    over the next 12 months.
                  </div>
                </div>
              </div>

              <div
                class="flex justify-between items-center z-50 p-2 pr-8 pl-8 text-gray-500 bg-white text-lg shadow border-l border-r border-b"
              >
                <div>
                  <div
                    title="Reset form"
                    class="cursor-pointer hover:text-gray-700 transition-all duration-500"
                    style="font-size: 0.97rem; margin-top: 2px; margin-bottom: -2px"
                    @click="reset()"
                  >
                    <i class="fas fa-undo leading-none" />
                  </div>
                </div>

                <div class="flex items-center">
                  <div
                    title="Copy link to clipboard"
                    class="mr-1 cursor-pointer hover:text-gray-700 transition-all duration-500"
                    style="font-size: 0.95rem; margin-bottom: 0px; padding-right: 5px"
                    @click="copyLink()"
                  >
                    <i class="fas fa-link leading-none" />
                  </div>
                  <a
                    :href="
                      'mailto:?subject=IX membership platform simulator&body=' + getLink()
                    "
                  >
                    <div
                      title="Send via email"
                      class="ml-1 cursor-pointer hover:text-gray-700 transition-all duration-500"
                    >
                      <i class="fas fa-envelope leading-none" />
                    </div>
                  </a>
                </div>
              </div>

              <div class="p-8 pr-7 border border-t-0 bg-white b-lg">
                <div class="flex">
                  <div class="flex w-2/3 pr-6">
                    <div class="w-1/2 pr-6">
                      <div class="mb-10 text-gray-500 focus-within:text-gray-800">
                        <div class="text-sm font-medium transition-all duration-500">
                          <div
                            v-if="flexiJoinersValid !== true"
                            class="-mt-5 text-sm text-red-600"
                          >
                            {{ flexiJoinersValid }}
                          </div>
                          Current flexi members
                          <div
                            class="inline-block tool"
                            data-tooltip="The current number of flexible members at your club."
                          >
                            <i
                              class="fad fa-info-circle duration-250 transition-all cursor-pointer"
                            />
                          </div>
                        </div>
                        <div
                          class="pt-1 pb-1 border-b focus-within:border-b-2 transition-all duration-500 focus-within:border-gray-800"
                        >
                          <input
                            v-model="currentFlexiMembers"
                            type="number"
                            class="outline-none text-gray-700 group-focus:text-gray-900 text-lg w-full"
                          />
                        </div>
                      </div>

                      <div class="mb-10">
                        <div class="text-sm font-medium text-gray-500 focus-within:text-gray-800">
                          Current flexible membership cohort
                          <div
                            class="inline-block tool"
                            data-tooltip="IX cohorts: 3 (£1,500-£1,699), 36 (£1,700-£1,899), 4 (£1,900-£2,150), 6 (£2,151-£2,500)"
                          >
                            <i class="fad fa-info-circle duration-250 transition-all cursor-pointer" />
                          </div>
                          <!-- <div
                            class="inline-block tool"
                            data-tooltip="The proportional cost of your flexible membership in comparison to your traditional membership."
                          >
                            <i class="fad fa-info-circle duration-250 transition-all cursor-pointer" />
                          </div> -->
                        </div>
                        <div @click="cohortDropdownOpen = !cohortDropdownOpen">
                          <div
                            class="flex justify-between items-center pt-1 pb-1 text-gray-600 hover:text-gray-800 select-none cursor-pointer text-lg border-b focus-within:border-b-2 transition-all duration-500"
                          >
                            <div>Cohort {{ flexiblePackageCost }}</div>
                            <div class="mr-2">
                              <i class="fas fa-chevron-down text-base" />
                            </div>
                          </div>
                        </div>

                        <div
                          v-if="cohortDropdownOpen"
                          class="relative w-full"
                        >
                          <div
                            class="absolute mt-2 z-40 border shadow-lg bg-white w-full overflow-hidden"
                          >
                            <div
                              v-for="cohort in visibleCohorts"
                              :key="cohort.index"
                              class="flex justify-between items-center p-2 pl-3 pr-3 bg-white hover:bg-gray-100 transition-all duration-200 cursor-pointer"
                              @click="
                                flexiblePackageCost = cohorts[cohort.index];
                                cohortDropdownOpen = false;
                              "
                            >
                              <div>Cohort {{ cohort.value }}</div>
                              <div
                                v-if="flexiblePackageCost === cohort.value"
                                class="mr-2"
                              >
                                <i class="far fa-check" />
                              </div>
                            </div>
                          </div>
                        </div>

                        <div
                          v-if="cohortDropdownOpen"
                          class="fixed top-0 left-0 z-30 w-full min-w-full h-full min-h-full"
                          @click="cohortDropdownOpen = false"
                        />
                      </div>

                      <div
                        v-if="showAllFields"
                        class="text-gray-500 focus-within:text-gray-800"
                      >
                        <div class="text-sm font-medium transition-all duration-500">
                          Employee hourly cost
                          <div
                            class="inline-block tool"
                            data-tooltip="The hourly cost of an employee including national insurance and pension contributions."
                          >
                            <i
                              class="fad fa-info-circle duration-250 transition-all cursor-pointer"
                            />
                          </div>
                        </div>
                        <div
                          class="flex pt-1 pb-1 text-gray-600 focus-within:text-gray-900 text-lg border-b focus-within:border-b-2 transition-all duration-500 focus-within:border-secondary-100"
                        >
                          <div>£{{ hourlyRate }}</div>
                        </div>
                      </div>
                    </div>

                    <div class="w-1/2">
                      <div class="mb-10 text-gray-500 focus-within:text-gray-800">
                        <div class="text-sm font-medium transition-all duration-500">
                          Current IX Partnership Fee
                          <div
                            class="inline-block tool"
                            data-tooltip="The price of your partnership fee with IX."
                          >
                            <i
                              class="fad fa-info-circle duration-250 transition-all cursor-pointer"
                            />
                          </div>
                        </div>
                        <div
                          class="flex items-center pt-1 pb-1 text-gray-600 focus-within:text-gray-900 text-lg border-b focus-within:border-b-2 transition-all duration-500 focus-within:border-secondary-100"
                        >
                          <div>£</div>
                          <input
                            v-model.number="pmgPartnershipFee"
                            type="number"
                            class="outline-none w-full bg-gray-100"
                            disabled
                            readonly
                          />
                          <div class="text-xs text-gray-500 ml-2">
                            (No partnership fee for IX)
                          </div>
                        </div>
                        <div class="text-xs text-gray-500 mt-1 flex items-center">
                          <span>Annual fee: £0 (No annual fee for IX)</span>
                        </div>
                      </div>

                      <div class="mb-10 text-gray-500 focus-within:text-gray-800">
                        <div class="flex items-center">
                          <div
                            class="bg-white hover:bg-gray-100 transition-all duration-500 border w-4 h-4 leading-none cursor-pointer flex justify-center items-center mr-2"
                            @click="contraEnabled = !contraEnabled"
                          >
                            <i
                              class="fas text-xs text-gray-600 fa-check"
                              :class="contraEnabled ? '' : 'opacity-0'"
                              style="margin-top: 2px"
                            />
                          </div>
                          <div class="text-sm font-medium transition-all duration-500">
                            Contra first {{ contraMembersPerYear }} members per year
                            <div
                              class="inline-block tool"
                              data-tooltip="Contra the first 4 members per year - this will adjust the calculations to account for contra members."
                            >
                              <i
                                class="fad fa-info-circle duration-250 transition-all cursor-pointer"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div class="text-gray-500 focus-within:text-gray-800">
                        <div class="text-sm font-medium transition-all duration-500">
                          <div
                            v-if="socialSpendValid !== true"
                            class="-mt-5 text-sm text-red-600"
                          >
                            {{ socialSpendValid }}
                          </div>
                          Monthly digital marketing spend
                          <div
                            class="inline-block tool"
                            data-tooltip="Your total monthly budget on social media including paid adverts/boosted posts and agency costs."
                          >
                            <i
                              class="fad fa-info-circle duration-250 transition-all cursor-pointer"
                            />
                          </div>
                        </div>
                        <div
                          class="flex pt-1 pb-1 text-gray-600 focus-within:text-gray-900 text-lg border-b focus-within:border-b-2 transition-all duration-500 focus-within:border-secondary-100"
                        >
                          <div>£</div>
                          <input
                            v-model="ownFlexi.marketingSpendMonth"
                            type="number"
                            class="outline-none w-full"
                          />
                        </div>
                      </div>

                      <div
                        v-if="false"
                        class="mt-10 text-gray-500 focus-within:text-gray-800"
                      >
                        <div class="text-sm font-medium transition-all duration-500">
                          <div
                            v-if="socialSpendValid !== true"
                            class="-mt-5 text-sm text-red-600"
                          >
                            {{ socialSpendValid }}
                          </div>
                          Monthly Digital Marketing Spend
                          <div
                            class="inline-block tool"
                            data-tooltip="Your total monthly budget on social media including paid adverts/boosted posts and agency costs."
                          >
                            <i
                              class="fad fa-info-circle duration-250 transition-all cursor-pointer"
                            />
                          </div>
                        </div>
                        <div
                          class="flex pt-1 pb-1 text-gray-600 focus-within:text-gray-900 text-lg border-b focus-within:border-b-2 transition-all duration-500 focus-within:border-secondary-100"
                        >
                          <div>£</div>
                          <input
                            v-model="ownFlexi.marketingSpendMonth"
                            type="number"
                            class="outline-none"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="w-1/3 pl-0">
                    <div class="mb-10 text-gray-500 focus-within:text-gray-800">
                      <div class="text-sm font-medium transition-all duration-500">
                        Additional license fee costs
                        <div
                          class="inline-block tool"
                          data-tooltip="The total sum of any annual licensing fees you would have to pay for your flexible module/add-on through your tee sheet provider."
                        >
                          <i
                            class="fad fa-info-circle duration-250 transition-all cursor-pointer"
                          />
                        </div>
                      </div>
                      <div
                        class="flex pt-1 pb-1 text-gray-600 focus-within:text-gray-900 text-lg border-b focus-within:border-b-2 transition-all duration-500 focus-within:border-secondary-100"
                      >
                        <div>£</div>
                        <input
                          v-model="ownFlexi.licenceFee"
                          type="number"
                          class="outline-none w-full"
                        />
                      </div>
                    </div>

                    <div class="my-10 text-gray-500 focus-within:text-gray-800">
                      <div class="text-sm font-medium transition-all duration-500">
                        <div
                          v-if="marketingSpendValid !== true"
                          class="-mt-5 text-sm text-red-600"
                        >
                          {{ marketingSpendValid }}
                        </div>
                        Monthly other marketing costs
                        <div
                          class="inline-block tool"
                          data-tooltip="Your total monthly budget for all other marketing activities including printed adverts, design work etc."
                        >
                          <i
                            class="fad fa-info-circle duration-250 transition-all cursor-pointer"
                          />
                        </div>
                      </div>
                      <div
                        class="flex pt-1 pb-1 text-gray-600 focus-within:text-gray-900 text-lg border-b focus-within:border-b-2 transition-all duration-500 focus-within:border-secondary-100"
                      >
                        <div>£</div>
                        <input
                          v-model="ownFlexi.marketingSpendOtherMonth"
                          type="number"
                          class="outline-none text-gray-700 group-focus:text-gray-900 text-lg w-full"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div :class="showAllFields ? 'mt-10' : 'mt-6'">
                  <div
                    class="w-full min-w-full text-sm p-4 pl-4 pr-4 text-center font-medium transition-all duration-500 text-white select-none cursor-pointer uppercase"
                    :class="
                      validForm
                        ? 'bg-gray-800 hover:bg-gray-900'
                        : 'bg-gray-300 text-gray-700 cursor-not-allowed'
                    "
                    @click="simulate()"
                  >
                    Run our membership platform simulator
                    <span v-if="club !== null">for {{ club.ClubName }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            class="p-2 text-2xl text-center mt-40 mb-6 transition-all duration-500 linear-wipe font-bold"
            :class="loading ? 'opacity-100' : 'opacity-0'"
          >
            Running 20,000 simulations
          </div>

          <div>
            <Chart
              :comparison-results="comparisonResults"
              :years="years"
              :selected-tab="selectedTab"
            />
          </div>

          <div
            v-if="comparisonResults !== null && comparisonResults !== undefined"
            class="max-w-4xl ml-auto mr-auto mt-12 pb-12"
          >
            <Table
              :comparison-results="comparisonResults"
              :years="years"
              :link="getLink()"
              :selected-tab-prop="selectedTab"
              :annual-fee="annualFee"
              @selectTab="selectTab"
              @updateAnnualFee="annualFee = $event"
            />
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
  import VueSlider from 'vue-slider-component';
  import { compareFlexibleProducts } from '@/app/shared/services/calculatorService/calculatorService';
  import { submitForm } from '@/app/shared/services/hubspotService/hubspotService';
  import { GetClub } from '@/app/shared/services/clubService/clubService';
  import LoadingView from '@/app/shared/components/base/loading/loadingView.vue';
  import Chart from '@/app/shared/components/ix/partner/chart/chart.vue';
  import Table from '@/app/shared/components/ix/partner/table/table.vue';
  import 'vue-slider-component/theme/antd.css';

  export default {
    name: 'Calculator',

    components: {
      Chart,
      Table,
      VueSlider,
      LoadingView,
    },

    data() {
      return {
        currency: '£',
        years: 1,
        formatter1: '{value}%',
        selectedTab: 'Summary',
        showAllFields: false,
        shareMenuOpen: false,
        // IX cohorts: 3, 36, 4, 6
        cohorts: [3, 36, 4, 6],
        visibleCohorts: [
          { value: 3, index: 0 },
          { value: 36, index: 1 },
          { value: 4, index: 2 },
          { value: 6, index: 3 },
        ],
        cohortDropdownOpen: false,

        pmgPartnershipFee: 0, // No partnership fee for IX
        annualFee: 0, // No annual fee for IX
        annualFeeWaivedMonths: 0,
        flexiblePackageCost: 3, // Default to cohort 3 for IX (display only; API gets price via cohort mapping)
        hourlyRate: 17.92,
        currentFlexiMembers: 40,
        contraEnabled: false, // Contra option: contra first 4 members per year
        contraMembersPerYear: 4,

        tooltip: { isVisible: true, format: 'C2' },
        ownFlexi: {
          pmgProduct: false,
          licenceFee: 1800,
          emailHours: 2.5,
          retentionModifier: 0.1,
          earningsReceived: 1,
          marketingSpendMonth: 350,
          marketingSpendOtherMonth: 150,
          cardFee: 0.03,
        },

        ixFlexi: {
          pmgProduct: true,
          licenceFee: 0, // No licence fee for IX
          emailHours: 0,
          retentionModifier: 0,
          earningsReceived: 0, // Will be calculated based on tiered commission
          marketingSpendMonth: 0,
          marketingSpendOtherMonth: 0,
          cardFee: 0,
        },

        comparisonResults: null,
        loading: false,
        linkCopied: false,
        club: null,
      };
    },

    computed: {
      // Calculate tiered commission rate based on member count
      commissionRate() {
        const members = this.currentFlexiMembers || 0;
        if (members >= 1 && members <= 30) {
          return 0.18; // 18%
        }
        if (members >= 31 && members <= 60) {
          return 0.14; // 14%
        }
        if (members >= 61) {
          return 0.10; // 10%
        }
        return 0.18; // Default to 18%
      },

      // Calculate earnings received based on tiered commission
      earningsReceived() {
        return 1 - this.commissionRate;
      },

      clubLoading() {
        if (
          this.$route.params.clubId !== null
          && this.$route.params.clubId > 0
          && this.club === null
        ) {
          return true;
        }

        return false;
      },

      flexiJoinersValid() {
        if (this.currentFlexiMembers >= 200) {
          return 'Value too high';
        }

        return true;
      },

      marketingSpendValid() {
        if (this.ownFlexi.marketingSpendMonth <= 0) {
          return 'Must be greater than 0';
        }
        return true;
      },

      socialSpendValid() {
        if (this.ownFlexi.marketingSpendOtherMonth <= 0) {
          return 'Must be greater than 0';
        }
        return true;
      },

      validForm() {
        if (
          this.flexiJoinersValid == true
          && this.marketingSpendValid === true
          && this.socialSpendValid === true
        ) {
          return true;
        }
        return false;
      },
    },

    watch: {
      flexiblePackageCost() {
        return 0.62;
      },

      linkCopied() {
        setTimeout(() => {
          this.linkCopied = false;
        }, 7000);
      },
    },

    mounted() {
      this.getClub();
      this.assignParams();
    },

    methods: {
      getClub() {
        const { clubId } = this.$route.params;

        const { query } = this.$route;

        if (clubId !== null && clubId > 0) {
          GetClub(clubId).then((data) => {
            this.club = data;

            if (this.$route.query.useLiveData) {
              if (data.Authorized) {
                this.currentFlexiMembers = data.CurrentFlexiMembers;
              }
              // For IX, cohorts are limited to 3, 36, 4, 6
              // Map the cohort index to IX cohorts if needed
              const cohortIndex = data.Cohort || 0;
              if (cohortIndex < this.cohorts.length) {
                this.flexiblePackageCost = this.cohorts[cohortIndex];
              }
            }
          });
        }
      },

      simulate() {
        if (!this.loading) {
          this.loading = true;
          this.comparisonResults = null;

          compareFlexibleProducts(this.getOwnFlexi(), this.getIXFlexi(), this.years, true)
            .then((data) => {
              // Ensure data is properly sorted by year
              if (data.Product1 && Array.isArray(data.Product1)) {
                data.Product1.sort((a, b) => a.Year - b.Year);
              }
              if (data.Product2 && Array.isArray(data.Product2)) {
                data.Product2.sort((a, b) => a.Year - b.Year);
              }
              setTimeout(() => (this.loading = false), 2000);
              // For single year, extract first element; for multiple years, keep array
              if (this.years === 1) {
                data.Product1 = data.Product1[0];
                data.Product2 = data.Product2[0];
              }
              setTimeout(() => (this.comparisonResults = data), 2000);
            })
            .catch((error) => {
              console.error('Error running simulation:', error);
              this.loading = false;
              // Show user-friendly error message
              if (error.message && error.message.includes('timeout')) {
                alert('The simulation is taking longer than expected. Please try again or contact support if the issue persists.');
              } else {
                alert('An error occurred while running the simulation. Please try again.');
              }
            });
        }
      },

      getIXFlexi() {
        const ixFlexi = { ...this.ixFlexi };
        // API expects flexiblePackageCost * flexiblePackageCostProportion (0.33)
        const price = this.getIXPackagePriceFromCohort(this.flexiblePackageCost);
        ixFlexi.flexiblePackageCost = price * 0.33;
        ixFlexi.pmgPartnershipFee = this.pmgPartnershipFee; // 0 for IX
        ixFlexi.hourlyRate = this.hourlyRate;
        ixFlexi.SocialSpendMonth = this.ixFlexi.marketingSpendOtherMonth;
        ixFlexi.currentFlexiMembers = this.currentFlexiMembers;
        ixFlexi.earningsReceived = this.earningsReceived;
        ixFlexi.commissionRate = this.commissionRate;
        ixFlexi.annualFee = 0; // No annual fee for IX
        ixFlexi.annualFeeWaivedMonths = 0;
        ixFlexi.contraEnabled = this.contraEnabled;
        ixFlexi.contraMembersPerYear = this.contraMembersPerYear;
        // Pass flexiblePackageCostProportion for API compatibility
        ixFlexi.flexiblePackageCostProportion = 33; // Default 33% proportion
        return ixFlexi;
      },

      // Map IX cohort ID to package price (midpoint of range) for API revenue calculation
      getIXPackagePriceFromCohort(cohortId) {
        const cohortToPrice = {
          3: 1600,   // £1,500-£1,699
          36: 1800,  // £1,700-£1,899
          4: 2025,   // £1,900-£2,150
          6: 2325,   // £2,151-£2,500
        };
        return cohortToPrice[cohortId] !== undefined ? cohortToPrice[cohortId] : 1600;
      },

      getOwnFlexi() {
        const ownFlexi = { ...this.ownFlexi };
        // API expects flexiblePackageCost * flexiblePackageCostProportion (0.33)
        const price = this.getIXPackagePriceFromCohort(this.flexiblePackageCost);
        ownFlexi.flexiblePackageCost = price * 0.33;
        ownFlexi.pmgPartnershipFee = this.pmgPartnershipFee;
        ownFlexi.hourlyRate = this.hourlyRate;
        ownFlexi.SocialSpendMonth = this.ownFlexi.marketingSpendOtherMonth;
        ownFlexi.currentFlexiMembers = this.currentFlexiMembers;
        return ownFlexi;
      },

      assignParams() {
        const { query } = this.$route;
        if (query.showAllFields !== undefined) {
          this.showAllFields = query.showAllFields === 'true';
        }

        if (query.licenseFee !== undefined) {
          this.ownFlexi.licenceFee = parseInt(query.licenseFee);
        }

        if (query.cohort !== undefined) {
          const cohort = parseInt(query.cohort, 10);
          if ([3, 36, 4, 6].includes(cohort)) {
            this.flexiblePackageCost = cohort;
          }
        }

        if (query.pmgFee !== undefined) {
          this.pmgPartnershipFee = parseInt(query.pmgFee);
        }

        if (query.hourlyRate !== undefined) {
          this.hourlyRate = parseFloat(query.hourlyRate);
        }

        if (query.currentFlexi !== undefined) {
          this.currentFlexiMembers = parseInt(query.currentFlexi);
        }

        if (query.monthlyMarketing !== undefined) {
          this.ownFlexi.marketingSpendMonth = parseInt(query.monthlyMarketing);
        }

        if (query.monthlyMarketingOther !== undefined) {
          this.ownFlexi.marketingSpendOtherMonth = parseInt(query.monthlyMarketingOther);
        }

        // const flexiProportion = urlParams.get('flexiProportion');
        // if (flexiProportion !== null) {
        //   this.flexiblePackageCostProportion = parseInt(flexiProportion);
        // }
      },

      reset() {
        const r = confirm('Are you sure you want to reset the form to the default values?');
        if (r === true) {
          this.pmgPartnershipFee = 0; // No partnership fee for IX
          this.annualFee = 0; // No annual fee for IX
          this.flexiblePackageCost = 3; // Default to cohort 3 for IX
          this.hourlyRate = 17.92;
          if (this.club) {
            this.currentFlexiMembers = this.club.CurrentFlexiMembers;
          }
          this.ownFlexi.licenceFee = 1800;
          this.ownFlexi.marketingSpendMonth = 350;
          this.ownFlexi.marketingSpendOtherMonth = 150;
        }
      },

      copyLink() {
        const textArea = document.createElement('textarea');
        textArea.style.position = 'fixed';
        textArea.style.top = 0;
        textArea.style.left = 0;
        textArea.style.width = '2em';
        textArea.style.height = '2em';
        textArea.style.padding = 0;
        textArea.style.border = 'none';
        textArea.style.outline = 'none';
        textArea.style.boxShadow = 'none';
        textArea.style.background = 'transparent';
        textArea.value = `${process.env.VUE_APP_SITE_URL}#/IX/Partner/${this.$route.params.clubId}?currentFlexi=${this.currentFlexiMembers}&licenseFee=${this.ownFlexi.licenceFee}&pmgFee=${this.pmgPartnershipFee}&cohort=${this.flexiblePackageCost}&monthlyMarketing=${this.ownFlexi.marketingSpendMonth}&monthlyMarketingOther=${this.ownFlexi.marketingSpendOtherMonth}`;
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
          document.execCommand('copy');
          // const msg = successful ? 'successful' : 'unsuccessful';
          this.linkCopied = true;
        } catch (err) {
          console.log('Oops, unable to copy');
        }
        document.body.removeChild(textArea);
      },

      getLink() {
        // const link = `${process.env.VUE_APP_SITE_URL}?licenseFee%3D${this.ownFlexi.licenceFee}%26fullMember%3D${this.fullMemberCost}%26currentFlexi%3D${this.currentFlexiMembers}%26monthlyMarketing%3D${this.ownFlexi.marketingSpendMonth}%26monthlyMarketing%3D${this.ownFlexi.marketingSpendOtherMonth}`;
        // const link = `${process.env.VUE_APP_SITE_URL}?licenseFee=${this.ownFlexi.licenceFee}&fullMember=${this.fullMemberCost}&hourlyRate=${this.hourlyRate}&currentFlexi=${this.currentFlexiMembers}&monthlyMarketing=${this.ownFlexi.marketingSpendMonth}&monthlySocial=${this.ownFlexi.socialSpendMonth}`;
        const link = `${process.env.VUE_APP_SITE_URL}#/IX/Partner/${this.$route.params.clubId}?currentFlexi%3D${this.currentFlexiMembers}%26licenseFee%3D${this.ownFlexi.licenceFee}%26pmgFee%3D${this.pmgPartnershipFee}%26cohort%3D${this.flexiblePackageCost}%26monthlyMarketing%3D${this.ownFlexi.marketingSpendMonth}%26monthlyMarketingOther%3D${this.ownFlexi.marketingSpendOtherMonth}`;
        return link;
      },

      selectTab(tab) {
        this.selectedTab = tab;
      },
    },
  };
</script>

<style lang="scss">
  .linear-wipe {
    text-align: center;

    background: linear-gradient(to right, #bbd673 20%, #add24e 40%, #add24e 60%, #bbd673 80%);
    background-size: 200% auto;

    color: #000;
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;

    animation: shine 2s linear infinite;
    @keyframes shine {
      to {
        background-position: 200% center;
      }
    }
  }

  .vue-slider-process {
    background-color: #add24e;
  }

  .vue-slider:hover .vue-slider-process {
    transition: all;
    transition-duration: 500ms;
    background-color: #add24e;
  }

  .vue-slider-dot-handle {
    border: 2px solid #add24e;
  }

  .vue-slider:hover .vue-slider-dot-handle:hover {
    border-color: #add24e;
  }

  .vue-slider-dot-handle-focus {
    border-color: rgb(248, 168, 74);
    -webkit-box-shadow: 0 0 0 5px rgba(248, 168, 74, 0.2);
    box-shadow: 0 0 0 5px rgba(248, 168, 74, 0.2);
  }
</style>

<style src="../../assets/site.css"></style>
